import React from "react";
import useTheme from "../../../../common/hooks/useTheme";
import { formatDisplayDate } from "../../../../common/utils/date";
import { Theme } from "../../../../providers/ThemeProvider";
import { useAuth } from "../../../../providers/AuthProvider";
import {
  enReportServiceType,
  enSvcUDurationCategory,
  enTimeUnit,
  enSvcUBillingType,
} from "../seviceBuilder/helper";
import { FIELD_CONFIG } from "../seviceBuilder/fieldConfig";
import { renderAttributeValue } from "./attributeUtils";
import type { Location, ServiceDetails } from "../seviceBuilder/types";
import InfoSvg from "../../../../common/svgs/InfoSvg";

interface ServiceDetailItemProps {
  label: string;
  value: string | React.ReactNode;
  useWarningStyle?: boolean;
}

const ServiceDetailItem: React.FC<ServiceDetailItemProps> = ({
  label,
  value,
  useWarningStyle = false,
}) => {
  const theme: Theme = useTheme();

  if (value === "" || value === null || value === undefined) {
    return null;
  }

  return (
    <div style={styles.detailElement}>
      <span style={styles.detailLabel(theme)}>{label}</span>
      <span style={styles.detailValue(theme, useWarningStyle)}>{value}</span>
    </div>
  );
};

interface ServiceInformationDetailsProps {
  service: ServiceDetails;
  prescriptionEndDate?: string;
}

const ServiceInformationDetails: React.FC<ServiceInformationDetailsProps> = ({
  service,
  prescriptionEndDate,
}) => {
  const theme: Theme = useTheme();
  const { AuthUser } = useAuth();

  // Check if service type shows PNOE and additional benefits
  const showBenefitsFields =
    enReportServiceType[service.serviceType] !== "Lab work" &&
    enReportServiceType[service.serviceType] !== "Medical procedures";

  // Helper function to format active ingredients
  const formatActiveIngredients = (): string => {
    if (!service.activeIngredients || service.activeIngredients.length === 0) {
      return "";
    }

    return service.activeIngredients
      .map((ingredient) => {
        const { name, value, min, max, unit } = ingredient;

        // Check if it's a range or single value
        const hasRange =
          (min !== null && min !== 0) || (max !== null && max !== 0);
        const hasSingleValue = value !== null && value !== 0;

        let valueStr = "";
        if (hasSingleValue && !hasRange) {
          // Single value: "2% Dextrose"
          valueStr = `${value}${unit || ""}`;
        } else if (hasRange && !hasSingleValue) {
          // Range: "2-10% Dextrose"
          const minVal = min || 0;
          const maxVal = max || 0;
          valueStr = `${minVal}-${maxVal}${unit || ""}`;
        }

        return valueStr ? `${valueStr} ${name}` : "";
      })
      .filter(Boolean)
      .join(", ");
  };

  // Get selected location titles
  const getSelectedLocationTitles = (): string => {
    if (!service.selectedLocations || !AuthUser?.locations) {
      return "No locations selected";
    }

    const selectedLocationIds = service.selectedLocations
      .split(",")
      .filter((id: string) => id.trim());

    if (selectedLocationIds.length === 0) {
      return "No locations selected";
    }

    const selectedLocationTitles = selectedLocationIds
      .map((locationId: string) => {
        const location = (AuthUser.locations as Location[])?.find(
          (loc: Location) => loc.id === locationId
        );
        return location?.title || null;
      })
      .filter((title: string | null) => title !== null);

    return selectedLocationTitles.length > 0
      ? selectedLocationTitles.join(", ")
      : "No locations selected";
  };

  // Get PNOE locator price display
  const getPNOELocatorPriceDisplay = (): string => {
    if (service.locatorVisible !== 1) {
      return "Not visible on locator";
    }

    if (!service.basePrice || service.basePrice <= 0) {
      return "Price not set";
    }

    const currency = service.currency || "$ USD";
    const billingType = service.billingType
      ? enSvcUBillingType[service.billingType]
      : "None";

    return `${currency.replace("USD", "").trim()} ${
      service.basePrice
    } / ${billingType.toLowerCase()}`;
  };

  // Check if service has expired (durationCategory=2 and calendarRangeTo is in the past)
  const isServiceExpired =
    service.durationCategory === 2 &&
    service.calendarRangeTo &&
    service.calendarRangeTo !== "0001-01-01T00:00:00" &&
    new Date(service.calendarRangeTo) < new Date();

  return (
    <>
      {service.active === 0 && (
        <div style={styles.noLongerAvailable(theme, theme.colorTokens.P[10])}>
          <div style={styles.noLongerAvailableHeader(theme)}>
            <InfoSvg color={theme.colors_ds.NEUTRAL_TEXT_DEFAULT} size={12} />
            <p>Service No Longer Available</p>
          </div>
          <div style={styles.noLongerAvailableMessage(theme)}>
            This service has been removed from the service builder but remains
            part of the client's prescription.
          </div>
        </div>
      )}

      {prescriptionEndDate && isServiceExpired && (
        <div style={styles.noLongerAvailable(theme, theme.colorTokens.D[10])}>
          <div style={styles.noLongerAvailableHeader(theme)}>
            <InfoSvg color={theme.colors_ds.NEUTRAL_TEXT_DEFAULT} size={12} />
            <p>Service No Longer Available</p>
          </div>
          <div style={styles.noLongerAvailableMessage(theme)}>
            This service expired on{" "}
            <span style={{ fontFamily: "var(--font-Bold)" }}>
              {formatDisplayDate(service.calendarRangeTo)}
            </span>
            . However, the prescription is still active until{" "}
            <span style={{ fontFamily: "var(--font-Bold)" }}>
              {formatDisplayDate(prescriptionEndDate)}
            </span>
            . The service remains visible in the client's prescription.
          </div>
        </div>
      )}

      <div style={styles.detailElementsWrapper}>
        {showBenefitsFields && service.metrics?.length > 0 && (
          <ServiceDetailItem
            label="PNOE benefits"
            value={service.metrics.map((b) => b.metric).join(", ")}
          />
        )}

        {showBenefitsFields && (
          <ServiceDetailItem
            label="Predefined benefit(s)"
            value={(() => {
              const activeBenefits = service.additionalBenefits
                ?.filter((b) => b.active === 1)
                ?.map((b) => b.metric)
                ?.filter(Boolean);

              return activeBenefits?.length > 0
                ? activeBenefits.join(", ")
                : "";
            })()}
          />
        )}

        {showBenefitsFields && (
          <ServiceDetailItem
            label="Custom benefit(s)"
            value={(() => {
              const activeBenefits = service.userAdditionalBenefits
                ?.map((b) => b.benefit)
                ?.filter(Boolean);

              return activeBenefits?.length > 0
                ? activeBenefits.join(", ")
                : "";
            })()}
          />
        )}

        {/* Render service attributes */}
        {service.attributes?.map((attribute, index) => (
          <ServiceDetailItem
            key={attribute.id || attribute.attributeID || index}
            label={attribute.name}
            value={renderAttributeValue(attribute)}
          />
        ))}

        {/* Active Ingredients for specific service types */}
        <ServiceDetailItem
          label={
            enReportServiceType[service.serviceType] === "Supplements"
              ? "Active Substance(s)/ Formula Type"
              : enReportServiceType[service.serviceType] === "Pharmaceuticals"
              ? "Active Substance(s)"
              : "Active Compound(s)"
          }
          value={formatActiveIngredients()}
        />

        {FIELD_CONFIG.filter(
          (field) =>
            !field.serviceTypes ||
            field.serviceTypes.includes(
              enReportServiceType[service.serviceType]
            )
        ).map((field, index: number) => {
          const fieldValue = service[field.key as keyof ServiceDetails];
          const displayValue =
            typeof fieldValue === "string" || typeof fieldValue === "number"
              ? fieldValue
              : "";

          return (
            <ServiceDetailItem
              key={index}
              label={field.title}
              value={displayValue}
            />
          );
        })}

        <ServiceDetailItem
          useWarningStyle={Boolean(isServiceExpired)}
          label="Date range"
          value={((): string => {
            const { durationCategory, calendarRangeFrom, calendarRangeTo } =
              service;

            switch (durationCategory) {
              case 3:
                return enSvcUDurationCategory[3];
              case 2:
                if (calendarRangeFrom && calendarRangeTo) {
                  const startDate =
                    calendarRangeFrom !== "0001-01-01T00:00:00"
                      ? formatDisplayDate(calendarRangeFrom)
                      : null;
                  const endDate =
                    calendarRangeTo !== "0001-01-01T00:00:00"
                      ? formatDisplayDate(calendarRangeTo)
                      : null;

                  if (startDate && endDate) {
                    return `${startDate} - ${endDate}`;
                  }
                }
                return enSvcUDurationCategory[2];
              default:
                return "Not specified";
            }
          })()}
        />

        <ServiceDetailItem
          label="Buffer time"
          value={((): string => {
            const { bufferTimeBefore, bufferTimeAfter } = service;
            const beforeText =
              bufferTimeBefore !== null && bufferTimeBefore >= 0
                ? `${bufferTimeBefore} min before`
                : null;
            const afterText =
              bufferTimeAfter !== null && bufferTimeAfter >= 0
                ? `${bufferTimeAfter} min after`
                : null;

            if (beforeText && afterText) {
              return `${beforeText} / ${afterText}`;
            } else if (beforeText) {
              return beforeText;
            } else if (afterText) {
              return afterText;
            } else {
              return "Not specified";
            }
          })()}
        />

        <ServiceDetailItem
          label="Pre-appointment notice"
          value={((): string => {
            const { preAppointmentNotice, preAppointmentNoticeType } = service;

            if (preAppointmentNotice) {
              const timeUnit = enTimeUnit[preAppointmentNoticeType] || "Hours";
              return `${preAppointmentNotice} ${timeUnit.toLowerCase()}`;
            }

            return "Not specified";
          })()}
        />

        <ServiceDetailItem
          label="Location(s)"
          value={getSelectedLocationTitles()}
        />

        <ServiceDetailItem
          label="PNOĒ locator price"
          value={getPNOELocatorPriceDisplay()}
        />
      </div>
    </>
  );
};

export default ServiceInformationDetails;

const styles = {
  detailElementsWrapper: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr 1fr",
    gap: "16px 24px",
  },
  detailElement: {
    display: "flex",
    flexDirection: "column" as "column",
    gap: "4px",
  },
  detailLabel: (theme: Theme) => ({
    fontFamily: "var(--font-Regular)",
    fontSize: "12px",
    lineHeight: "14px",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
  }),
  detailValue: (theme: Theme, useWarningStyle: boolean) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "14px",
    lineHeight: "20px",
    color: useWarningStyle ? "red" : theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
  }),
  noLongerAvailable: (theme: Theme, bgColor: string) => ({
    backgroundColor: bgColor,
    borderRadius: "8px",
    padding: "12px",
    border: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
    marginBottom: "16px",
  }),
  noLongerAvailableHeader: (theme: Theme) => ({
    display: "flex",
    alignItems: "center",
    gap: "8px",
    fontFamily: "var(--font-Medium)",
    fontSize: "14px",
    lineHeight: "20px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
    marginBottom: "8px",
  }),
  noLongerAvailableMessage: (theme: Theme) => ({
    fontFamily: "var(--font-Regular)",
    fontSize: "12px",
    lineHeight: "20px",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
    marginLeft: "20px",
  }),
};
